import { useState, useEffect } from 'react';
import { authAPI, companiesAPI } from '@/services/api';

interface UserData {
  id: number;
  email: string;
  fullName: string;
  phoneNumber?: string;
  company_id: number;
  role?: string;
  branch_id?: number | null;
  branch_name?: string | null;
  company?: {
    id: number;
    name: string;
    phone?: string;
    email?: string;
    address?: string;
  };
}

export const useUserData = () => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Try to get user data from localStorage first
        const storedUserData = localStorage.getItem('stockwise-user-cache');
        let currentUserData: UserData | null = null;
        
        if (storedUserData) {
          const parsedData = JSON.parse(storedUserData);
          currentUserData = parsedData;
          setUserData(parsedData);
          
          // If we have basic user data but no company info, fetch fresh user data first
          if (!parsedData.company) {
            try {
              const freshUserData = await authAPI.getCurrentUser();
              currentUserData = freshUserData;
              setUserData(freshUserData);
              localStorage.setItem('stockwise-user-cache', JSON.stringify(freshUserData));
            } catch (err) {
              console.warn('Failed to fetch fresh user data:', err);
              // Keep using stored data even if fresh fetch fails
            }
          }
        } else {
          // No stored data, fetch from API
          const freshUserData = await authAPI.getCurrentUser();
          currentUserData = freshUserData;

          // Fetch company in parallel if needed
          if (!freshUserData.company && freshUserData.company_id) {
            try {
              const companyData = await companiesAPI.getCompany(freshUserData.company_id);
              currentUserData = { ...freshUserData, company: { id: companyData.id, name: companyData.name, phone: companyData.phone, email: companyData.email, address: companyData.address } };
            } catch (err) {
              console.warn('Failed to fetch company data:', err);
            }
          }

          setUserData(currentUserData);
          localStorage.setItem('stockwise-user-cache', JSON.stringify(currentUserData));
        }
        
        // If we still don't have company info but have a company_id, fetch company separately
        if (currentUserData && !currentUserData.company && currentUserData.company_id) {
          try {
            const companyData = await companiesAPI.getCompany(currentUserData.company_id);
            const updatedUserData = {
              ...currentUserData,
              company: { id: companyData.id, name: companyData.name, phone: companyData.phone, email: companyData.email, address: companyData.address }
            };
            setUserData(updatedUserData);
            localStorage.setItem('stockwise-user-cache', JSON.stringify(updatedUserData));
          } catch (err) {
            console.warn('Failed to fetch company data:', err);
            const signupCompanyName = localStorage.getItem('temp-company-name');
            if (signupCompanyName) {
              const updatedUserData = { ...currentUserData, company: { id: currentUserData.company_id, name: signupCompanyName, phone: undefined, email: undefined, address: undefined } };
              setUserData(updatedUserData);
              localStorage.setItem('stockwise-user-cache', JSON.stringify(updatedUserData));
              localStorage.removeItem('temp-company-name');
            }
          }
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we have a token
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, []);

  const refreshUserData = async () => {
    try {
      setLoading(true);
      const freshUserData = await authAPI.getCurrentUser();
      let currentUserData = freshUserData;
      
      // If we don't have company info but have a company_id, fetch company separately
      if (!freshUserData.company && freshUserData.company_id) {
        try {
          console.log('Refreshing company info for company_id:', freshUserData.company_id);
          const companyData = await companiesAPI.getCompany(freshUserData.company_id);
          
          currentUserData = {
            ...freshUserData,
            company: {
              id: companyData.id,
              name: companyData.name,
              phone: companyData.phone,
              email: companyData.email,
              address: companyData.address,
            }
          };
        } catch (err) {
          console.warn('Failed to fetch company data during refresh:', err);
          // Continue with user data without company info
        }
      }
      
      setUserData(currentUserData);
      localStorage.setItem('stockwise-user-cache', JSON.stringify(currentUserData));
    } catch (err) {
      console.error('Error refreshing user data:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh user data');
    } finally {
      setLoading(false);
    }
  };

  return {
    userData,
    loading,
    error,
    refreshUserData,
    // Helper getters
    userName: userData?.fullName || 'User',
    userEmail: userData?.email || '',
    companyName: userData?.company?.name || 'StockWise',
    // The data scope this user is limited to. Admins can see every branch;
    // everyone else is bound to their assigned branch (or all, if unassigned).
    branchName: userData?.role === 'admin' ? null : (userData?.branch_name || null),
    userInitials: userData?.fullName 
      ? userData.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
      : 'U',
  };
};